import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shared review console for challenge briefs and network applications.
export default function AdminSubmissions({ kind }) {
  const isChallenge = kind === "challenge";
  const { user } = useAuth();
  const endpoint = isChallenge ? "/admin/challenge-submissions" : "/admin/network-applications";
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get("ref");
  const [statuses, setStatuses] = useState([]);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [openedRef, setOpenedRef] = useState(null);
  const [note, setNote] = useState("");
  const [decMsg, setDecMsg] = useState("");
  const [decInvite, setDecInvite] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(endpoint, { params: filter ? { status: filter } : {} }).then(({ data }) => {
      setStatuses(data.statuses);
      setItems(data.results);
    });
  };
  useEffect(load, [filter, endpoint]);

  // Deep-link: open the application referenced in ?ref= once it has loaded.
  useEffect(() => {
    if (refParam && refParam !== openedRef && items.length) {
      const found = items.find((i) => i.reference === refParam);
      if (found) {
        setActive(found);
        setOpenedRef(refParam);
      }
    }
  }, [refParam, items, openedRef]);

  const update = async (ref, patch) => {
    try {
      const { data } = await api.patch(`${endpoint}/${ref}`, patch);
      toast.success("Updated");
      setActive(data);
      setNote("");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteItem = async () => {
    if (!active) return;
    if (!window.confirm(`Permanently delete ${active.reference}? This cannot be undone.`)) return;
    try {
      await api.delete(`${endpoint}/${active.reference}`);
      toast.success("Application deleted");
      setActive(null);
      setOpenedRef(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const sendDecision = async (decision) => {
    if (!active) return;
    setSending(true);
    try {
      const { data } = await api.post(`${endpoint}/${active.reference}/decision`, {
        decision,
        message: decMsg,
        include_build_invite: decInvite,
      });
      if (data.email_sent) toast.success(`Applicant emailed — marked ${decision === "approved" ? "Approved for network" : "Declined"}`);
      setActive(data.application);
      setDecMsg("");
      setDecInvite(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send decision");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-bold text-navy">{isChallenge ? "Challenge briefs" : "Network applications"}</h1>
        {isChallenge && (
          <a href={`${API}/admin/export/challenge-submissions.csv`} data-testid="export-csv" className="rounded-full border border-[var(--line)] px-4 py-2 text-sm font-semibold text-navy hover:border-[var(--cobalt)]">Export CSV</a>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full border px-3 py-1 text-xs ${!filter ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white" : "border-[var(--line)] text-navy"}`}>All</button>
        {statuses.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-full border px-3 py-1 text-xs ${filter === s ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white" : "border-[var(--line)] text-navy"}`}>{s}</button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        <div className="flex flex-col gap-3">
          {items.length === 0 && <p className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--paper)] p-8 text-center text-sm text-[var(--mineral)]">No submissions yet.</p>}
          {items.map((it) => (
            <button key={it.reference} onClick={() => setActive(it)} data-testid={`submission-${it.reference}`}
              className={`rounded-2xl border bg-[var(--paper)] p-5 text-left ${active?.reference === it.reference ? "border-[var(--cobalt)]" : "border-[var(--line)]"}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-[var(--mineral)]">{it.reference}</span>
                <span className="rounded-full bg-[var(--ivory)] px-2 py-0.5 font-mono text-[0.66rem] text-navy">{it.status}</span>
              </div>
              <p className="mt-2 font-display font-semibold text-navy">{isChallenge ? it.title : it.full_name}</p>
              <p className="text-sm text-[var(--mineral)]">{isChallenge ? it.organization : it.primary_discipline}</p>
            </button>
          ))}
        </div>

        {active && (
          <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6" data-testid="submission-detail">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-[var(--mineral)]">{active.reference}</span>
              <select value={active.status} onChange={(e) => update(active.reference, { status: e.target.value })} data-testid="submission-status-select" className="rounded-full border border-[var(--line)] px-3 py-1 text-sm outline-none focus:border-[var(--cobalt)]">
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {isChallenge ? (
                <>
                  <Row k="Title" v={active.title} />
                  <Row k="Organisation" v={active.organization} />
                  <Row k="Contact" v={`${active.full_name} · ${active.work_email}`} />
                  <Row k="Country" v={active.country} />
                  <Row k="Problem" v={active.problem} />
                  <Row k="Why now" v={active.why_now} />
                  <Row k="Domains" v={(active.domains || []).join(", ")} />
                  <Row k="Outcomes" v={(active.desired_outcomes || []).join(", ")} />
                </>
              ) : (
                <>
                  <Row k="Name" v={active.full_name} />
                  <Row k="Email" v={active.email} />
                  <Row k="Title" v={active.title} />
                  <Row k="Primary discipline" v={active.primary_discipline} />
                  <Row k="Summary" v={active.summary} />
                  <Row k="Public opt-in" v={active.public_optin ? "Yes" : "No"} />
                </>
              )}
            </div>

            <div className="mt-5 border-t border-[var(--line)] pt-4">
              <p className="eyebrow mb-2">Internal notes</p>
              <div className="space-y-2">
                {(isChallenge ? active.internal_notes : active.reviewer_notes)?.map((n, i) => (
                  <div key={i} className="rounded-lg bg-[var(--ivory)] p-3 text-sm">
                    <span className="text-navy">{n.note}</span>
                    <span className="mt-1 block font-mono text-[0.62rem] text-[var(--mineral)]">{n.by}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" placeholder="Add an internal note" className="flex-1 rounded-xl border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--cobalt)]" />
                <button onClick={() => note && update(active.reference, { note })} data-testid="note-submit" className="rounded-full bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white">Add</button>
              </div>
            </div>

            {!isChallenge && (
              <div className="mt-5 border-t border-[var(--line)] pt-4" data-testid="decision-panel">
                <p className="eyebrow mb-2">Notify applicant of a decision</p>
                <p className="mb-3 text-xs text-[var(--mineral)]">Sends an official DBRF email to {active.email} and sets the status. Any message below is included in the email.</p>
                <textarea value={decMsg} onChange={(e) => setDecMsg(e.target.value)} data-testid="decision-message" rows={3} placeholder="Optional additional message to the applicant (e.g. next steps)…" className="w-full rounded-xl border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--cobalt)]" />
                <label className="mt-2 flex items-start gap-2 text-sm text-navy">
                  <input type="checkbox" checked={decInvite} onChange={(e) => setDecInvite(e.target.checked)} data-testid="decision-invite" className="mt-1 h-4 w-4 accent-[var(--cobalt)]" />
                  <span>Include an invitation to the Emergent app building environment (with rules of use). Recommended for approvals only.</span>
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => sendDecision("approved")} disabled={sending} data-testid="decision-approve" className="rounded-full bg-[var(--success)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                    {sending ? "Sending…" : "Approve & email"}
                  </button>
                  <button onClick={() => sendDecision("declined")} disabled={sending} data-testid="decision-decline" className="rounded-full border border-[var(--error)] px-4 py-2 text-sm font-semibold text-[var(--error)] disabled:opacity-60">
                    {sending ? "Sending…" : "Decline & email"}
                  </button>
                </div>
              </div>
            )}

            {user?.role === "administrator" && (
              <div className="mt-5 border-t border-[var(--line)] pt-4">
                <button onClick={deleteItem} data-testid="delete-submission" className="rounded-full border border-[var(--error)] px-4 py-2 text-sm font-semibold text-[var(--error)] hover:bg-[var(--error)] hover:text-white">
                  Delete application
                </button>
                <p className="mt-2 text-xs text-[var(--mineral)]">Permanently removes this record and its history. Administrators only.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2">
      <dt className="text-[var(--mineral)]">{k}</dt>
      <dd className="text-navy">{v || "—"}</dd>
    </div>
  );
}
